// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
// #region TerceraMultiComboBoxPopup
import TerceraMultiComboBoxPopupTemplate from '@front/templates/TerceraMultiComboBoxPopupTemplate.html';
import { HtmlScroll } from '@components/webdesktop/elements/Scroll/HtmlScroll';
import { ContainerControl } from './ContainerControl';
import { CheckBoxCheckedStates } from './TerceraCheckBox';
import { Rectangle } from '@shared/commons/Geometry';
import { Resources } from '@shared/localizations/Resources';
import { multiComboBoxPopupMenuHandler } from '@shared/utils/AppHandlers';
import { ControlsUtils } from '../UtilsClasses/ControlsUtils';
export class TerceraMultiComboBoxPopup extends ContainerControl {
    constructor() {
        super(...arguments);
        this.lastIndex = null;
        this.onItemCheckBoxClicked = (event, item) => {
            const isChecked = event.currentTarget.checked;
            const items = this.get('items');
            const index = items.indexOf(item);
            void this.set('items[' + index + '].checked', isChecked);
        };
        this.onAllCheckBoxClicked = (event) => {
            const isAllChecked = event.currentTarget.checked;
            const items = this.get('items');
            if (!isValidArray(items)) {
                return;
            }
            items.forEach((item) => {
                void this.set('items[' + items.indexOf(item) + '].checked', isAllChecked);
            });
        };
    }
    oninit() {
        super.oninit();
        this.observe('items', this.onItemsChanged);
        this.observe('checkBoxCheckedState', this.onCheckBoxCheckedStateChanged);
    }
    onItemsChanged(items) {
        if (!isValidArray(items)) {
            void this.set('checkBoxCheckedState', CheckBoxCheckedStates.None);
            return;
        }
        let checkedCount = 0;
        const len = items.length;
        let lastIndex = null;
        for (let i = 0; i < len; i++) {
            const item = items[i];
            if (item.checked) {
                checkedCount++;
                lastIndex = i;
            }
        }
        if (!checkedCount && this.lastIndex != null && this.get('useLastItem')) {
            items[this.lastIndex].checked = true;
            void this.set('items', items);
            return;
        }
        this.lastIndex = lastIndex;
        if (checkedCount === len) {
            void this.set('isAllChecked', true);
            void this.set('checkBoxCheckedState', CheckBoxCheckedStates.Checked);
        }
        else {
            void this.set('isAllChecked', false);
            void this.set('checkBoxCheckedState', CheckBoxCheckedStates.None);
        }
    }
    onCheckBoxCheckedStateChanged(val) {
        const items = this.get('items');
        if (!isValidArray(items)) {
            return;
        }
        if (val === CheckBoxCheckedStates.None && items.some((item) => item.checked)) {
            return;
        }
        const check = val === CheckBoxCheckedStates.Checked;
        let valueChanged = false;
        for (let i = 0, len = items.length; i < len; i++) {
            const item = items[i];
            if (isNullOrUndefined(item) || item.checked === check) {
                continue;
            }
            item.checked = check;
            valueChanged = true;
        }
        if (valueChanged) {
            void this.update('items');
        }
    }
    Hide() {
        var _a;
        this.lastIndex = null;
        if (!isNullOrUndefined(this.callBack)) {
            this.callBack(this.get('items'));
            delete this.callBack;
        }
        void this.set('items', null);
        void this.set('visible', false);
        void this.set('hideAll', false);
        void this.set('useLastItem', false);
        if (this.scrollIsShown) {
            (_a = this.scroll) === null || _a === void 0 ? void 0 : _a.destroy();
            this.scrollIsShown = false;
        }
    }
    Show(items, x, y, width, callBack, hideAll, useLastItem) {
        this.localize();
        void this.set('items', items);
        void this.set('hideAll', hideAll);
        void this.set('useLastItem', useLastItem);
        this.callBack = callBack;
        void this.set('visible', true);
        const popupWidth = Math.max(TerceraMultiComboBoxPopup.MIN_WIDTH, width);
        const itemHeight = 32;
        let height = items ? (items.length + 1) * itemHeight : 0;
        if (hideAll) {
            height -= itemHeight;
        }
        if (height > 280) {
            height = 280;
            this.scrollIsShown = true;
        }
        const newLoc = ControlsUtils.CorrectPopupLocation(new Rectangle(x, y, popupWidth, height));
        this.setBounds(newLoc.newX, newLoc.newY, popupWidth, height);
        if (this.scrollIsShown) {
            this.scroll = HtmlScroll.addScroll(this.find('.items-container'));
        }
    }
    localize() {
        void this.set('allText', Resources.getResource('PopupMultiCkeckBox.All'));
    }
    ProcessOnMouseDown(caller) {
        const callerParent = caller.parent;
        if (callerParent !== this) {
            this.Hide();
        }
    }
    onteardown() {
        var _a;
        (_a = this.scroll) === null || _a === void 0 ? void 0 : _a.destroy();
        super.onteardown();
    }
    static MultiComboBoxPopupHandlerInitialize() {
        const multiComboBoxPopup = multiComboBoxPopupMenuHandler;
        multiComboBoxPopup.Init = () => {
            const _multiComboBoxPopup = new TerceraMultiComboBoxPopup();
            multiComboBoxPopup.Show = _multiComboBoxPopup.Show.bind(_multiComboBoxPopup);
            multiComboBoxPopup.Hide = _multiComboBoxPopup.Hide.bind(_multiComboBoxPopup);
            multiComboBoxPopup.ProcessOnMouseDown = _multiComboBoxPopup.ProcessOnMouseDown.bind(_multiComboBoxPopup);
            multiComboBoxPopup.isShowed = () => {
                return _multiComboBoxPopup.get('visible');
            };
        };
    }
}
TerceraMultiComboBoxPopup.MIN_WIDTH = 200;
// #endregion
ContainerControl.extendWith(TerceraMultiComboBoxPopup, {
    el: '#multiComboBoxContainer',
    data: function () {
        return {
            allText: '',
            items: null,
            visible: false,
            useLastItem: false,
            checkBoxCheckedState: CheckBoxCheckedStates.None,
            isAllChecked: false
        };
    },
    template: TerceraMultiComboBoxPopupTemplate
});
